import React, { useState, useEffect, useMemo } from 'react'
import apiClient, { API_ENDPOINTS } from '../../config/api'

const StockMovementHistory = () => {
  const [movements, setMovements] = useState([])
  const [filteredMovements, setFilteredMovements] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [filters, setFilters] = useState({
    search: '',
    type: '',
    status: '',
    dateFrom: '',
    dateTo: ''
  })

  useEffect(() => {
    const fetchMovements = async () => {
      setLoading(true)
      setError('')

      try {
        const response = await apiClient.get(API_ENDPOINTS.STOCK_TRANSFERS)
        setMovements(response.data || [])
        setFilteredMovements(response.data || [])
      } catch (err) {
        console.error('Failed to fetch stock movements:', err)
        setError('Unable to load stock movement data. Please try again later.')
      } finally {
        setLoading(false)
      }
    }

    fetchMovements()
  }, [])

  useEffect(() => {
    let filtered = [...movements]

    if (filters.search) {
      const searchLower = filters.search.toLowerCase()
      filtered = filtered.filter(movement => {
        const fields = [
          movement.transfer_type_display,
          movement.status_display,
          movement.reference_number,
          movement.source_warehouse_name,
          movement.source_branch_name,
          movement.destination_warehouse_name,
          movement.destination_branch_name
        ]
        return fields
          .filter(Boolean)
          .some(field => field.toLowerCase().includes(searchLower))
      }
      )
    }

    if (filters.type) {
      filtered = filtered.filter(movement => movement.transfer_type === filters.type)
    }

    if (filters.status) {
      const statusFilter = filters.status.toLowerCase()
      filtered = filtered.filter(movement => (movement.status || '').toLowerCase() === statusFilter)
    }

    if (filters.dateFrom) {
      filtered = filtered.filter(movement =>
        new Date(movement.created_at) >= new Date(filters.dateFrom)
      )
    }

    if (filters.dateTo) {
      const endDate = new Date(filters.dateTo)
      endDate.setHours(23, 59, 59, 999)
      filtered = filtered.filter(movement =>
        new Date(movement.created_at) <= endDate
      )
    }

    setFilteredMovements(filtered)
  }, [movements, filters])

  const movementTypes = useMemo(() => {
    const unique = new Map()
    movements.forEach(movement => {
      if (!unique.has(movement.transfer_type)) {
        unique.set(movement.transfer_type, movement.transfer_type_display || movement.transfer_type)
      }
    })
    return Array.from(unique.entries()).map(([value, label]) => ({ value, label }))
  }, [movements])

  const statusOptions = useMemo(() => {
    const unique = new Map()
    movements.forEach(movement => {
      const value = movement.status
      const label = movement.status_display || value
      if (value && !unique.has(value)) {
        unique.set(value, label || value)
      }
    })
    return Array.from(unique.entries()).map(([value, label]) => ({ value, label }))
  }, [movements])

  const formatCurrency = (value) =>
    typeof value === 'number'
      ? `$${value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
      : '$0.00'

  const isCompleted = (movement) =>
    movement?.status === 'completed' ||
    movement?.status_display?.toLowerCase() === 'completed'

  const handleMovementClick = (movement) => {
    const event = new CustomEvent('navigateToScreen', {
      detail: {
        screen: 'stock-movement-history-detail',
        params: { movement }
      }
    })
    window.dispatchEvent(event)
  }

  return (
    <>
      <div className="pagetitle">
        <h1>Stock Movement</h1>
        <nav>
          <ol className="breadcrumb">
            <li className="breadcrumb-item"><a href="/">Home</a></li>
            <li className="breadcrumb-item">Products</li>
            <li className="breadcrumb-item active">Stock Movement</li>
          </ol>
        </nav>
      </div>

      <section className="section">
        <div className="row">
          <div className="col-lg-12">
            <div className="card">
              <div className="card-body">
                <div className="mb-3">
                  <h5 className="card-title mb-0">
                    <i className="bi bi-arrow-left-right me-2"></i>
                    Stock Movement History
                  </h5>
                </div>

                {/* Filters */}
                <div className="row mb-3 g-3">
                  <div className="col-md-3">
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Search movements..."
                      value={filters.search}
                      onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                    />
                  </div>
                  <div className="col-md-2">
                    <select
                      className="form-select"
                      value={filters.type}
                      onChange={(e) => setFilters({ ...filters, type: e.target.value })}
                    >
                      <option value="">All Types</option>
                      {movementTypes.map(type => (
                        <option key={type.value} value={type.value}>
                          {type.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="col-md-2">
                    <select
                      className="form-select"
                      value={filters.status}
                      onChange={(e) => setFilters({ ...filters, status: e.target.value })}
                    >
                      <option value="">All Statuses</option>
                      {statusOptions.map(status => (
                        <option key={status.value} value={status.value}>
                          {status.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="col-md-2">
                    <input
                      type="date"
                      className="form-control"
                      placeholder="From Date"
                      value={filters.dateFrom}
                      onChange={(e) => setFilters({ ...filters, dateFrom: e.target.value })}
                    />
                  </div>
                  <div className="col-md-2">
                    <input
                      type="date"
                      className="form-control"
                      placeholder="To Date"
                      value={filters.dateTo}
                      onChange={(e) => setFilters({ ...filters, dateTo: e.target.value })}
                    />
                  </div>
                </div>

                {/* Movements Table */}
                {loading ? (
                  <p>Loading stock movement data...</p>
                ) : error ? (
                  <div className="alert alert-danger" role="alert">
                    {error}
                  </div>
                ) : filteredMovements.length === 0 ? (
                  <p>No stock movements match your filters.</p>
                ) : (
                  <div className="table-responsive">
                    <table className="table table-striped">
                      <thead>
                        <tr>
                          <th>Transfer Type</th>
                          <th>Total Cost</th>
                          <th>Source Warehouse</th>
                          <th>Destination Warehouse</th>
                          <th>Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredMovements.map(movement => (
                          <tr
                            key={movement.id}
                            role="button"
                            onClick={() => handleMovementClick(movement)}
                          >
                            <td>{movement.transfer_type_display || movement.transfer_type}</td>
                            <td>{formatCurrency(Number(movement.total_cost) || 0)}</td>
                            <td>{movement.source_warehouse_name || 'N/A'}</td>
                            <td>{movement.destination_warehouse_name || movement.destination_branch_name || 'N/A'}</td>
                            <td>
                              <span className={`badge ${
                                isCompleted(movement) ? 'bg-success' :
                                movement.status === 'pending' || movement.status_display === 'Pending' ? 'bg-warning text-dark' :
                                'bg-secondary'
                              }`}>
                                {movement.status_display || movement.status}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  )
}

export default StockMovementHistory





